import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, FolderOpen, MoreVertical, Edit, Trash2, GripVertical, ArrowRight } from 'lucide-react';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Toast from '../../components/Toast';
import { CYCLE_ICONS, CYCLE_ICON_LABELS } from '../../components/dashboard/ExploreContent';
import {
    getCollections,
    createCollection,
    updateCollection,
    deleteCollection,
    reorderCollections,
    getDecks,
} from '../services/userContent.service';
import { UserCollection } from '../types/userContent.types';
import './MyFlashcardsPage.css';

// ── Paleta de cores para coleções (tons azul/teal GRAIA) ────────────────────

const COLLECTION_COLORS: { id: string; top: string; bg: string; label: string }[] = [
    { id: 'blue-deep',    top: '#042563', bg: 'rgba(4,37,99,0.10)',    label: 'Azul Profundo' },
    { id: 'blue-vibrant', top: '#003BD1', bg: 'rgba(0,59,209,0.10)',   label: 'Azul Vibrante' },
    { id: 'blue-royal',   top: '#1E40AF', bg: 'rgba(30,64,175,0.10)',  label: 'Azul Royal' },
    { id: 'blue-medium',  top: '#2563EB', bg: 'rgba(37,99,235,0.10)',  label: 'Azul Médio' },
    { id: 'blue-sky',     top: '#3B82F6', bg: 'rgba(59,130,246,0.10)', label: 'Azul Claro' },
    { id: 'teal-night',   top: '#164E63', bg: 'rgba(22,78,99,0.10)',   label: 'Teal Noite' },
    { id: 'teal',         top: '#0F766E', bg: 'rgba(15,118,110,0.10)', label: 'Teal' },
    { id: 'teal-bright',  top: '#1D9E75', bg: 'rgba(29,158,117,0.10)', label: 'Verde-azulado' },
    { id: 'indigo',       top: '#4F46E5', bg: 'rgba(79,70,229,0.10)',  label: 'Índigo' },
    { id: 'slate',        top: '#334155', bg: 'rgba(51,65,85,0.10)',   label: 'Ardósia' },
];

function getColor(colorId: string) {
    return COLLECTION_COLORS.find(c => c.id === colorId) ?? COLLECTION_COLORS[0];
}

function getIcon(iconKey: string): React.ComponentType<any> {
    return (CYCLE_ICONS as any)[iconKey] ?? (CYCLE_ICONS as any)['BookOpen'];
}

// ── Card Menu ────────────────────────────────────────────────────────────────

interface CardMenuProps {
    onEdit: () => void;
    onDelete: () => void;
}
const CardMenu: React.FC<CardMenuProps> = ({ onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="mf-menu-wrap" ref={ref}>
            <button
                className="mf-menu-btn"
                onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
                title="Opções"
            >
                <MoreVertical size={15} />
            </button>
            {open && (
                <div className="mf-menu-dropdown" onClick={e => e.stopPropagation()}>
                    <button onClick={() => { onEdit(); setOpen(false); }}>
                        <Edit size={13} /> Editar
                    </button>
                    <button className="danger" onClick={() => { onDelete(); setOpen(false); }}>
                        <Trash2 size={13} /> Excluir
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Sortable Collection Card ─────────────────────────────────────────────────

interface SortableCardProps {
    col: UserCollection;
    masteryPct: number | undefined;
    onNavigate: (colId: string) => void;
    onEdit: (col: UserCollection) => void;
    onDelete: (col: UserCollection) => void;
}

const SortableCollectionCard: React.FC<SortableCardProps> = ({ col, masteryPct, onNavigate, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: col._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
    };

    const color = getColor(col.colorId);
    const Icon = getIcon(col.icon);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="mf-card"
            onClick={() => onNavigate(col._id)}
        >
            <div className="mf-card-top" style={{ background: color.top }} />
            <div className="mf-card-body">
                <div className="mf-card-header-row">
                    <div className="mf-card-icon" style={{ background: color.bg, color: color.top }}>
                        <Icon size={22} />
                    </div>
                    <p className="mf-card-name">{col.name}</p>
                    <div className="mf-card-actions" onClick={e => e.stopPropagation()}>
                        <button
                            className="mf-drag-handle"
                            title="Reordenar"
                            {...attributes}
                            {...listeners}
                        >
                            <GripVertical size={15} />
                        </button>
                        <CardMenu onEdit={() => onEdit(col)} onDelete={() => onDelete(col)} />
                    </div>
                </div>
                {col.description && (
                    <p className="mf-card-desc">{col.description}</p>
                )}
                <div className="mf-card-mastery">
                    <div className="mf-mastery-label-row">
                        <span className="mf-mastery-count">{col.deckCount ?? 0} deck{(col.deckCount ?? 0) !== 1 ? 's' : ''}</span>
                        {masteryPct !== undefined
                            ? <span className="mf-mastery-pct" style={{ color: color.top }}>{masteryPct}% domínio</span>
                            : <span className="mf-mastery-pct-empty">–</span>
                        }
                    </div>
                    <div className="mf-mastery-bar-bg">
                        <div
                            className="mf-mastery-bar-fill"
                            style={{ width: `${masteryPct ?? 0}%`, background: color.top }}
                        />
                    </div>
                </div>
                <div className="mf-card-footer">
                    <button
                        className="mf-enter-btn"
                        style={{ backgroundColor: color.top }}
                        onClick={e => { e.stopPropagation(); onNavigate(col._id); }}
                    >
                        Entrar <ArrowRight size={13} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Color Picker ─────────────────────────────────────────────────────────────

const ColorPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
    <div className="mf-color-row">
        {COLLECTION_COLORS.map(c => (
            <button
                key={c.id}
                type="button"
                className={`mf-color-dot${value === c.id ? ' selected' : ''}`}
                style={{ background: c.top }}
                onClick={() => onChange(c.id)}
                title={c.label}
            />
        ))}
    </div>
);

// ── Icon Picker ───────────────────────────────────────────────────────────────

const IconPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
    <div className="mf-icon-grid">
        {Object.entries(CYCLE_ICONS).map(([key]) => {
            const Icon = getIcon(key);
            const label = (CYCLE_ICON_LABELS as any)[key] || key;
            return (
                <button
                    key={key}
                    type="button"
                    className={`mf-icon-opt${value === key ? ' selected' : ''}`}
                    onClick={() => onChange(key)}
                    title={label}
                >
                    <Icon size={20} />
                </button>
            );
        })}
    </div>
);

// ── Skeleton ──────────────────────────────────────────────────────────────────

const SkeletonGrid: React.FC = () => (
    <div className="mf-grid">
        {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="mf-skeleton-card">
                <div className="mf-skeleton-top" />
                <div className="mf-skeleton-body">
                    <div className="mf-skeleton-line wide" />
                    <div className="mf-skeleton-line narrow" />
                </div>
            </div>
        ))}
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

type ModalMode = 'create' | 'edit' | null;

interface FormState {
    name: string;
    description: string;
    colorId: string;
    icon: string;
}

const EMPTY_FORM: FormState = { name: '', description: '', colorId: 'blue-vibrant', icon: 'BookOpen' };

interface MyFlashcardsPageProps {
    onRegisterNewCollection?: (fn: () => void) => void;
}

const MyFlashcardsPage: React.FC<MyFlashcardsPageProps> = ({ onRegisterNewCollection }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [collections, setCollections] = useState<UserCollection[]>([]);
    const [loading, setLoading] = useState(true);
    const [collectionMastery, setCollectionMastery] = useState<Map<string, number>>(new Map());
    const [modalMode, setModalMode] = useState<ModalMode>(null);
    const [editTarget, setEditTarget] = useState<UserCollection | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [deleteTarget, setDeleteTarget] = useState<UserCollection | null>(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const openCreate = useCallback(() => {
        setForm(EMPTY_FORM);
        setEditTarget(null);
        setModalMode('create');
    }, []);

    useEffect(() => {
        onRegisterNewCollection?.(openCreate);
    }, [onRegisterNewCollection, openCreate]);

    useEffect(() => {
        fetchCollections();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function fetchCollections() {
        setLoading(true);
        try {
            const data = await getCollections();
            setCollections(data);
            loadMastery(data);
        } catch {
            showToast('Erro ao carregar coleções', 'error');
        } finally {
            setLoading(false);
        }
    }

    async function loadMastery(cols: UserCollection[]) {
        if (!cols.length) return;
        const token = localStorage.getItem('accessToken');
        const map = new Map<string, number>();
        await Promise.all(cols.map(async col => {
            try {
                const decks = await getDecks(col._id);
                if (!decks.length) { map.set(col._id, 0); return; }
                const masteries = await Promise.all(decks.map(async (d: any) => {
                    try {
                        const r = await fetch(`/api/user-content/study/${d._id}/stats`, {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!r.ok) return null;
                        const data = await r.json();
                        return typeof data.masteryPercentage === 'number' ? data.masteryPercentage : null;
                    } catch { return null; }
                }));
                const valid = masteries.filter((m): m is number => m !== null);
                if (valid.length) {
                    map.set(col._id, Math.round(valid.reduce((a, b) => a + b, 0) / valid.length));
                }
            } catch { /* ignore */ }
        }));
        setCollectionMastery(new Map(map));
    }

    function showToast(message: string, type: 'success' | 'error' | 'info') {
        setToast({ message, type });
    }

    function openEdit(col: UserCollection) {
        setForm({ name: col.name, description: col.description, colorId: col.colorId, icon: col.icon });
        setEditTarget(col);
        setModalMode('edit');
    }

    function closeModal() {
        setModalMode(null);
        setEditTarget(null);
    }

    async function handleSave() {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            if (modalMode === 'create') {
                const created = await createCollection(form);
                setCollections(prev => [...prev, created]);
                showToast('Coleção criada', 'success');
            } else if (editTarget) {
                const updated = await updateCollection(editTarget._id, form);
                setCollections(prev => prev.map(c => c._id === updated._id ? { ...c, ...updated } : c));
                showToast('Coleção atualizada', 'success');
            }
            closeModal();
        } catch (err: any) {
            showToast(err.message || 'Erro ao salvar', 'error');
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await deleteCollection(deleteTarget._id);
            setCollections(prev => prev.filter(c => c._id !== deleteTarget._id));
            showToast('Coleção excluída', 'success');
            setDeleteTarget(null);
        } catch (err: any) {
            showToast(err.message || 'Erro ao excluir', 'error');
        } finally {
            setSaving(false);
        }
    }

    function handleNavigate(colId: string) {
        const col = collections.find(c => c._id === colId);
        navigate(`/my-flashcards/${colId}`, {
            state: { backTo: location.pathname, collectionName: col?.name ?? '' },
        });
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = collections.findIndex(c => c._id === active.id);
        const newIndex = collections.findIndex(c => c._id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(collections, oldIndex, newIndex).map((c, i) => ({ ...c, order: i }));
        setCollections(reordered);
        reorderCollections(reordered.map(c => ({ _id: c._id, order: c.order }))).catch(() =>
            showToast('Erro ao salvar ordem', 'error')
        );
    }

    return (
        <div className="mf-page">
            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
            )}

            {loading ? (
                <SkeletonGrid />
            ) : collections.length === 0 ? (
                <div className="mf-empty">
                    <FolderOpen size={64} className="mf-empty-icon" />
                    <p className="mf-empty-title">Nenhuma coleção ainda</p>
                    <p className="mf-empty-sub">
                        Crie sua primeira coleção para começar a organizar seus flashcards
                    </p>
                    <button className="mf-btn-primary" onClick={openCreate}>
                        <Plus size={16} /> Criar primeira coleção
                    </button>
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={collections.map(c => c._id)} strategy={rectSortingStrategy}>
                        <div className="mf-grid">
                            {collections.map(col => (
                                <SortableCollectionCard
                                    key={col._id}
                                    col={col}
                                    masteryPct={collectionMastery.get(col._id)}
                                    onNavigate={handleNavigate}
                                    onEdit={openEdit}
                                    onDelete={c => setDeleteTarget(c)}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* ── Create / Edit Modal ── */}
            {modalMode && (
                <div className="mf-overlay" onClick={closeModal}>
                    <div className="mf-dialog" onClick={e => e.stopPropagation()}>
                        <p className="mf-dialog-title">
                            {modalMode === 'create' ? 'Nova Coleção' : 'Editar Coleção'}
                        </p>

                        <div className="mf-field">
                            <label className="mf-label">Nome</label>
                            <input
                                className="mf-input"
                                value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Ex: Matemática"
                                maxLength={100}
                                autoFocus
                            />
                        </div>

                        <div className="mf-field">
                            <label className="mf-label">Descrição</label>
                            <textarea
                                className="mf-textarea"
                                value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Descrição opcional..."
                                maxLength={500}
                            />
                        </div>

                        <div className="mf-field">
                            <label className="mf-label">Cor</label>
                            <ColorPicker value={form.colorId} onChange={v => setForm(f => ({ ...f, colorId: v }))} />
                        </div>

                        <div className="mf-field">
                            <label className="mf-label">Ícone</label>
                            <IconPicker value={form.icon} onChange={v => setForm(f => ({ ...f, icon: v }))} />
                        </div>

                        <div className="mf-dialog-actions">
                            <button className="mf-btn-cancel" onClick={closeModal}>Cancelar</button>
                            <button className="mf-btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                                {saving ? 'Salvando...' : modalMode === 'create' ? 'Criar' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirm Modal ── */}
            {deleteTarget && (
                <div className="mf-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="mf-dialog" onClick={e => e.stopPropagation()}>
                        <p className="mf-dialog-title">Excluir coleção</p>
                        <p className="mf-delete-msg">
                            Tem certeza que deseja excluir a coleção{' '}
                            <span className="mf-delete-name">"{deleteTarget.name}"</span>?
                            Todos os decks e flashcards dentro dela serão removidos permanentemente.
                        </p>
                        <div className="mf-dialog-actions">
                            <button className="mf-btn-cancel" onClick={() => setDeleteTarget(null)}>Cancelar</button>
                            <button className="mf-btn-danger" onClick={handleDelete} disabled={saving}>
                                {saving ? 'Excluindo...' : 'Excluir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyFlashcardsPage;
